import { SupplierRelationshipCreateResponseType } from "@sellar-io/api-v2/types";
import { SpinnerLegacy } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { BUYER } from "../../../../constants/routes";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../components/modal/Dialog";
import { useEvents } from "../../../../hooks";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { usePriceTierList } from "../../../../hooks/api/v2/priceTier/usePriceTierList";
import { useBuyer } from "../../../../hooks/api/v2/retailer/useBuyer";
import { useSupplierRelationshipCreate } from "../../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipCreate";
import { useParamAsNumber } from "../../../../hooks/useParamAsNumber";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { EditConnectionPropertiesFragment } from "./EditConnectionPropertiesFragment";

export const ConnectionSendInvitationModal = ({
  onSuccess,
  onDismiss,
  active,
}: {
  onSuccess: (connection: SupplierRelationshipCreateResponseType) => void;
  onDismiss: () => void;
  active: boolean;
}) => {
  const { track } = useEvents();
  const buyerId = useParamAsNumber(BUYER, "buyerId");

  const connectionCreateMutation = useSupplierRelationshipCreate();

  const { business } = useSellarCurrentBusiness();

  if (!business) throw new Error("Cannot find business");

  const supplier = business.Supplier;

  if (!supplier) throw new Error("Cannot find Supplier");
  if (!supplier) throw new Error("Cannot find Supplier");

  const buyerQuery = useBuyer(buyerId);

  const priceTiersListQuery = usePriceTierList({});

  const priceTiers = (
    priceTiersListQuery.data?.pages.flatMap((pt) => pt) ?? []
  ).filter((pt) => pt.derivationType !== "base");

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={"Assign terms"}
      />
      <DialogContent>
        <>
          {!priceTiersListQuery.data?.pages[0]?.length ? (
            <div>
              You need to create a price tier before accepting connections
            </div>
          ) : (
            <>
              {connectionCreateMutation.isError && (
                <Alert
                  type={"error"}
                  message={
                    apiV2AssertError(connectionCreateMutation.error)?.response
                      ?.data.message ?? "An unknown error occurred"
                  }
                />
              )}
              {priceTiersListQuery.isLoading ||
              connectionCreateMutation.isPending ||
              buyerQuery.isPending ? (
                <SpinnerLegacy />
              ) : (
                <EditConnectionPropertiesFragment
                  priceTiers={priceTiers}
                  onSubmit={async (e) => {
                    if (!e.PriceTierId) {
                      throw new Error("Price Tier not set");
                    }

                    if (!e.fulfillmentType) {
                      throw new Error("FulfillmentType not set");
                    }

                    if (!buyerId) {
                      throw new Error("Cannot find buyerId");
                    }

                    connectionCreateMutation.mutate(
                      {
                        SupplierId: supplier.id,
                        BuyerId: buyerId,
                        PriceTierId: parseInt(e.PriceTierId.toString()),
                        AvailabilityTierId: e.AvailabilityTierId
                          ? parseInt(e.AvailabilityTierId.toString())
                          : null,
                        OrderRulesId: e.OrderRulesId
                          ? parseInt(e.OrderRulesId.toString())
                          : null,
                        DrayDeliveryMethodId: e.DrayDeliveryMethodId
                          ? parseInt(e.DrayDeliveryMethodId.toString())
                          : null,
                        CourierDeliveryMethodId: e.CourierDeliveryMethodId
                          ? parseInt(e.CourierDeliveryMethodId.toString())
                          : null,
                        PalletDeliveryMethodId: e.PalletDeliveryMethodId
                          ? parseInt(e.PalletDeliveryMethodId.toString())
                          : null,
                        fulfillmentType: e.fulfillmentType,
                        initiator: "supplier",
                        buyerContactEmail: e.buyerContactEmail ?? null,
                        paymentDue: e.paymentDue ?? null,
                        vatPercentage:
                          e.vatPercentage || e.vatPercentage === 0
                            ? e.vatPercentage
                            : null,
                        paymentTerms: e.paymentTerms ?? null,
                        paymentDueCalculateFrom:
                          e.paymentDueCalculateFrom ?? null,
                        notes: e.notes,
                        customerCode: e.customerCode,
                      },
                      {
                        onSuccess: (data) => {
                          track("Connection Invitation Sent", {
                            initiator: "supplier",
                          });
                          onSuccess(data);
                        },
                      },
                    );
                  }}
                  buyerPostcode={
                    buyerQuery.data?.Business.DeliveryAddress.postalCode
                  }
                />
              )}
            </>
          )}
        </>
      </DialogContent>
    </Dialog>
  );
};
